import React, { ComponentProps, ReactElement, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity, View } from 'react-native';
import { Menu, MenuOptions, MenuTrigger, renderers } from 'react-native-popup-menu';
import { useOwnProfile } from '_api/useProfile';
import { useGetUser } from '_api/useUsers';
import useAppNavigation from '_navigator/hooks/useAppNavigation';
import { WW, useRerenderCountdown, openURL, useChatWithNeighbour } from '_utils';
import { track } from '_utils/Amplitude';
import { screenMargin, smallestFontSize, smallestMargin, smallestMarginPixels, subtitleFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { HeimeText, Icon, Loader, UserListItem } from 'Components';
import { OwnReservation } from 'types/Reservation';
import { isNonDeletedUser } from 'types/User';
import ReservationDurationView from './components/ReservationDurationView';

const ReservationDetail = ({
    start_at,
    end_at,
    reason,
    booked,
    quantity,
    previous_users,
    users,
    id,
    product_instances,
}: Pick<
    OwnReservation,
    'end_at' | 'start_at' | 'reason' | 'booked' | 'quantity' | 'previous_users' | 'users' | 'id' | 'product_instances'
>): ReactElement => {
    const { t } = useTranslation();
    const { data: ownProfile } = useOwnProfile();
    const start = moment.unix(start_at);
    const { navigate } = useAppNavigation();

    const { theme } = useThemeContext();

    useRerenderCountdown(start, true);

    const handleEditUsers = () => {
        track('Edit reservation users pressed');
        navigate('EditReservationUsers', { reservationId: id });
    };

    const showCapacity = booked.capacity > 1 && quantity !== product_instances.length;
    const showInstances = product_instances.filter((instance) => instance.label).length > 0;

    return (
        <>
            <View style={{ gap: smallestMargin, marginBottom: smallestMargin }}>
                {showCapacity ? (
                    <LabelWithIcon
                        label={t('expandedReservation:capacity')}
                        value={t('expandedReservation:quantity', { count: quantity })}
                        icon="layers"
                    />
                ) : null}
                {showInstances ? (
                    <LabelWithIcon
                        label={t('expandedReservation:instances')}
                        value={product_instances.map((instance) => instance.label).join(', ')}
                        icon="pin2"
                    />
                ) : null}
                {reason ? (
                    <LabelWithIcon
                        label={
                            booked.requires_license_plate
                                ? t('expandedReservation:license_plate')
                                : t('expandedReservation:reason')
                        }
                        value={reason}
                        icon={booked.requires_license_plate ? 'car' : 'editPencil'}
                    />
                ) : null}
            </View>
            <ReservationDurationView start_at={start_at} end_at={end_at} locksLength={(booked.locks ?? []).length} />
            {previous_users && previous_users.users.length > 0 ? (
                <View>
                    <View style={{ flexDirection: 'row', gap: smallestMargin }}>
                        <HeimeText style={{ fontWeight: 'bold', color: theme.black, fontSize: subtitleFontSize }}>
                            {t('expandedReservation:previous_visitor')}
                        </HeimeText>
                        <InformationBubble />
                    </View>
                    {previous_users.users.map((user) => (
                        <PreviousUser key={user} userId={user} />
                    ))}
                </View>
            ) : null}
            {users.length > 0 ? (
                <View>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                        <HeimeText style={{ fontWeight: 'bold', color: theme.black, fontSize: subtitleFontSize }}>
                            {t('expandedReservation:users', { count: users.length })}
                        </HeimeText>
                        <TouchableOpacity onPress={handleEditUsers} style={{ padding: smallestMargin }}>
                            <HeimeText variant="subtitle" style={{ color: theme.main, fontWeight: 'bold' }}>
                                {t('expandedReservation:edit')}
                            </HeimeText>
                        </TouchableOpacity>
                    </View>
                    <View>
                        {users.slice(0, 50).map((user) => {
                            const isOwnProfile = user === ownProfile?.id;
                            return (
                                <UserListItem
                                    key={user}
                                    id={user}
                                    noBottomBorder
                                    desc={isOwnProfile ? t('expandedReservation:you') : undefined}
                                    descColor={isOwnProfile ? theme.red : undefined}
                                    disableProfileNavigate={isOwnProfile}
                                />
                            );
                        })}
                    </View>
                </View>
            ) : null}
        </>
    );
};

const PreviousUser = ({ userId }: { userId: number }) => {
    const user = useGetUser(userId);
    const { data: profile } = useOwnProfile();
    const { t } = useTranslation();
    const { isLoading, onChat } = useChatWithNeighbour();
    const { theme } = useThemeContext();

    const handleChatWithNeighbour = () => onChat(userId);
    return (
        <UserListItem
            id={userId}
            actionButton={
                user && isNonDeletedUser(user) && userId !== profile?.id ? (
                    <View style={{ flexDirection: 'row', gap: smallestMargin, alignItems: 'center' }}>
                        {user?.hide_phone || !user?.phone ? null : (
                            <TouchableOpacity
                                accessibilityLabel={t('profile:call')}
                                style={{
                                    backgroundColor: theme.main,
                                    borderRadius: WW * 0.5,
                                    padding: smallestMarginPixels * 1.5,
                                }}
                                onPress={() => openURL(`tel:${user.phone}`, t)}
                            >
                                <Icon scale={1.2} name="call" color="white" />
                            </TouchableOpacity>
                        )}

                        <TouchableOpacity
                            accessibilityLabel={t('profile:message')}
                            onPress={handleChatWithNeighbour}
                            style={{
                                backgroundColor: theme.main,
                                borderRadius: WW * 0.5,
                                padding: smallestMarginPixels * 1.5,
                            }}
                        >
                            {isLoading ? (
                                <Loader bgColor="transparent" />
                            ) : (
                                <Icon scale={1.2} name="message" color="white" />
                            )}
                        </TouchableOpacity>
                    </View>
                ) : undefined
            }
        />
    );
};

const InformationBubble = () => {
    const [isOpen, setIsOpen] = useState(false);
    const onToggle = () => setIsOpen((prev) => !prev);
    const { t } = useTranslation();
    const { theme } = useThemeContext();

    return (
        <Menu
            renderer={renderers.Popover}
            rendererProps={{
                placement: 'auto',
                preferredPlacement: 'top',
                anchorStyle: { backgroundColor: theme.main },
            }}
            opened={isOpen}
            onBackdropPress={onToggle}
        >
            <MenuTrigger onPress={onToggle}>
                <TouchableOpacity
                    onPress={onToggle}
                    accessibilityLabel={t('expandedReservation:previous_visitor_action')}
                    hitSlop={16}
                >
                    <Icon name="question" color="main" />
                </TouchableOpacity>
            </MenuTrigger>
            <MenuOptions
                customStyles={{
                    optionsContainer: {
                        backgroundColor: theme.main,
                        borderRadius: screenMargin,
                        padding: smallestMargin,
                    },
                }}
            >
                <View style={{ flexDirection: 'row' }}>
                    <HeimeText
                        style={{
                            fontSize: smallestFontSize,
                            lineHeight: smallestFontSize * 1.5,
                            color: theme.white,
                            flexShrink: 1,
                        }}
                        maxFontSizeMultiplier={4}
                    >
                        {t('expandedReservation:previous_visitor_info')}
                    </HeimeText>
                    <TouchableOpacity
                        accessibilityLabel={t('global:close')}
                        onPress={onToggle}
                        style={{ marginLeft: 'auto', marginTop: -smallestMargin }}
                    >
                        <Icon name="cross" color="white" scale={0.6} />
                    </TouchableOpacity>
                </View>
            </MenuOptions>
        </Menu>
    );
};

const LabelWithIcon = ({
    label,
    value,
    icon,
}: {
    label: string;
    value: string;
    icon: ComponentProps<typeof Icon>['name'];
}) => {
    const { theme } = useThemeContext();
    return (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: smallestMargin }}>
            <Icon name={icon} color="secondaryText" />
            <HeimeText style={{ color: theme.secondaryText, fontSize: subtitleFontSize }}>{label}:</HeimeText>
            <HeimeText style={{ color: theme.darkGray, fontWeight: 'bold', flex: 1, fontSize: subtitleFontSize }}>
                {value}
            </HeimeText>
        </View>
    );
};

export default ReservationDetail;
